import { FormlyFieldConfig } from '@ngx-formly/core';

import { isEmptyValue } from '../../../utils/empty-value.util';
import { clearableIsReadOnly } from '../clearable-field.type';
import { isReadonlyWrappable, READONLY_WRAPPER, readonlyFieldValue, readonlyPresenceValue } from '../readonly/readonly.types';

/** Marks a read-only field so the field-state tokens can recess it (#20818). */
export const READONLY_FIELD_CLASS = 'dd-field-readonly';

/**
 * The single place that decides what read-only means for a field (#21239).
 *
 * Two outcomes, one skip matrix (`readonly/readonly.types.ts`):
 * - a field that can be shown as a label/value row gets the `readonly` wrapper as its OUTERMOST
 *   wrapper, so read mode replaces the whole field — form-field chrome included — instead of
 *   dressing up an input; when it is ALREADY read-only at build time it also gets its `hide`
 *   expression composed so an empty OPTIONAL field disappears from the page outright (task
 *   21245) — a required field stays visible and gets the "Ontbreekt" badge instead
 *   (`readonly-field-row.component.ts`);
 * - a field on the skip list (own view, opt-out, display-only, container) keeps drawing itself
 *   and only gets the marker class below.
 *
 * Registered as `prePopulate` AFTER `hover-tooltip`: Formly runs every `prePopulate` before any
 * `onPopulate` (ngx-formly-core.mjs:2314-2318), and `hover-tooltip` rewrites the whole wrapper
 * array (hover-tooltip.extension.ts:27-34) — running first would drop our wrapper again.
 */
export function readonlyExtension(field: FormlyFieldConfig): void {
  if (!field.props || field.key === undefined || field.key === null) {
    return;
  }

  if (!isReadonlyWrappable(field)) {
    stampReadonlyMarker(field);
    return;
  }

  // Re-entrant: Formly re-runs prePopulate when a field tree is rebuilt, and a second
  // `readonly` in the chain would render a read row inside a read row.
  const innerWrappers = (field.wrappers ?? []).filter(wrapper => wrapper !== READONLY_WRAPPER);
  field.wrappers = [READONLY_WRAPPER, ...innerWrappers];

  // ONE gate for both hide-related decisions below: the field is read-only at BUILD time.
  //
  // Read mode is a page-level state — `DetailsService` rebuilds the field tree when the page
  // switches — so a field that is read-only here stays read-only for this tree's lifetime, and a
  // field that is not can only ever run the empty-clause to `false`. Composing anyway would cost
  // every EDIT-mode field its normal `defaultValue` timing: Formly's `isHiddenField()` treats any
  // field carrying a `hide` expression as potentially hidden, which moves the default assignment
  // out of `initFieldOptions` into `changeHideState`.
  //
  // Consequence, and a deliberate one: should a field ever become read-only through an
  // EXPRESSION while the page is in edit mode, the wrapper still renders its read row (it re-reads
  // `props` every pass) but the field is never hidden when empty — in edit mode every field stays
  // on the page, which is what a form being filled in has to do. Rights, per-role "Uitschakelen"
  // and conditions no longer produce read-only at all: they keep the field a disabled input.
  if (!clearableIsReadOnly(field.props)) {
    return;
  }

  // Page-mode read-only is static per build, so an empty optional field can be hidden
  // outright (task 21245). Hiding must not drop the key from the model: interface-action
  // conditions and calculated values read the full entry while the page is in read mode.
  field.resetOnHide = false;

  composeEmptyFieldHide(field);
}

/** A compiled `hide` expression, called by Formly the way a plain function is documented to be
 *  called: with the field itself (see `composeEmptyFieldHide` below for the exact wiring proof). */
type HideFn = (field: FormlyFieldConfig) => boolean;

/**
 * Functions this extension has already composed — so a re-entrant `prePopulate` pass (the same
 * field tree rebuild the wrapper de-dup above guards against) recomposes nothing. Recomposing an
 * already-composed function would still be behaviourally correct (ORing the same clause onto
 * itself changes nothing), but it would nest one more closure per rebuild for the field's whole
 * lifetime — a `WeakSet` keeps this an O(1) idempotency check instead.
 */
const composedEmptyFieldHideFns = new WeakSet<HideFn>();

/**
 * Hides an empty OPTIONAL field outright in read mode (task 21245, design spec §4): a page full
 * of "—" rows for fields nobody filled in is noise, not information. A REQUIRED field stays
 * visible even when empty — its `dd-readonly-field-row` shows the "Ontbreekt" badge instead, and
 * hiding it here would bury exactly the gap a reader needs to notice.
 *
 * Composed as an OR onto whatever hide the field already carries — from EITHER of Formly's two
 * hide sources, `field.expressions.hide` or the older `field.hideExpression` (see below) — a
 * module-configured hide (role-based visibility, a conditional field) must keep hiding the field
 * for its own reasons regardless of read mode. Only the shapes `isComposableHide` accepts are
 * folded in; a STRING (either source) or an OBSERVABLE is left completely untouched, field and
 * all, for the reasons documented there.
 *
 * Only reached for a field that is already read-only at prePopulate time (see the gate in
 * `readonlyExtension` above). The condition still re-reads `props.readonly`/`props.required` off
 * the field on every call rather than closing over their prePopulate-time values: both are
 * routinely expression-driven (see `stampReadonlyMarker`'s doc below for `readonly`), so a field
 * whose read-only state is switched back off mid-tree short-circuits the whole clause to `false`,
 * leaving only the field's own pre-existing `hide` (if any) in effect.
 *
 * Wiring proof for why the composed function only needs the FIELD argument: when
 * `field.expressions.hide` is a plain function (as this one is) at `onPopulate` time, Formly
 * wraps it as `(...args) => expr(field, args[3])` before compiling it
 * (`FieldExpressionExtension$1.onPopulate`, ngx-formly-core.mjs:463-473) — so the function this
 * extension installs at `prePopulate`, one pass earlier, IS that raw `expr`, called with the
 * field (and an `ignoreCache` flag this clause has no use for) once the wrap runs.
 */
function composeEmptyFieldHide(field: FormlyFieldConfig): void {
  const existingHide = field.expressions?.['hide'];
  if (!isComposableHide(existingHide)) {
    return;
  }
  if (typeof existingHide === 'function' && composedEmptyFieldHideFns.has(existingHide as HideFn)) {
    return;
  }

  // `hideExpression` is Formly's OLDER hide API (still used by `fieldGroup.config.ts`'s
  // note/note-enabled pair) — a SECOND, separate hide source from `field.expressions.hide` above.
  // Formly's own `FieldExpressionExtension.onPopulate` assigns `_expressions.hide` from
  // `hideExpression` FIRST, then OVERWRITES it from `field.expressions.hide` when that key is
  // present (ngx-formly-core.mjs:453-456 vs :471-473 — last writer wins, and `observe()` runs both
  // callbacks synchronously so there is no ordering trick to exploit). Assigning our OWN function
  // to `field.expressions.hide` below therefore silently drops a configured `hideExpression`
  // unless it is folded into the composed function too — so it is read here and OR'd in exactly
  // like `existingHide`. A STRING `hideExpression` gets the same treatment `isComposableHide`
  // gives a string `expressions.hide`: no composition at all for this field, for the same reason —
  // Formly compiles a string itself and there is no safe way to fold a JS condition into an opaque
  // formula without reimplementing that compiler. `hideExpression` has no Observable form to guard
  // against (`boolean | string | fn`, fieldconfig.d.ts:95), which is why one string check is all
  // this source needs.
  const hideExpression = field.hideExpression;
  if (typeof hideExpression === 'string') {
    return;
  }

  const composedHide: HideFn = f =>
    evaluateHide(existingHide, f) ||
    evaluateHideExpression(hideExpression, f) ||
    (clearableIsReadOnly(f.props) && !f.props?.required && isEmptyValue(readonlyPresenceValue(f)));
  composedEmptyFieldHideFns.add(composedHide);

  field.expressions = { ...field.expressions, hide: composedHide };
}

/**
 * The three pre-existing `hide` shapes this extension can fold its own clause into: no hide at
 * all, a plain boolean, or a function. Formly TYPES the property as
 * `string | ((field) => boolean) | Observable<boolean>` (fieldconfig.d.ts:7,12), but a stored
 * module config reaches it as data, so a boolean does turn up at runtime — and Formly's own
 * `evalExpression` coerces one (ngx-formly-core.mjs:332-338), so folding it in is faithful.
 *
 * The other two shapes are left COMPLETELY alone, field and all: a STRING is a formula Formly
 * compiles itself (`_evalStringExpression`, :547-548) and an OBSERVABLE is a stream Formly
 * subscribes to — neither can be OR'd with a JS condition without reimplementing that half of
 * Formly. Coercing an Observable with `!!` (what this used to do by falling through) would have
 * hidden the field permanently, since any object is truthy.
 */
function isComposableHide(hide: unknown): boolean {
  return hide === undefined || typeof hide === 'boolean' || typeof hide === 'function';
}

/**
 * Runs a captured pre-existing `hide` the same way Formly's own `evalExpression` treats it
 * (ngx-formly-core.mjs:332-338): a function is called with the field, `undefined` and a boolean
 * are coerced directly. Nothing else can reach this — `isComposableHide` above is the gate.
 */
function evaluateHide(hide: unknown, field: FormlyFieldConfig): boolean {
  return typeof hide === 'function' ? !!(hide as HideFn)(field) : !!hide;
}

/**
 * Runs a captured `field.hideExpression` the way Formly itself calls it — NOT the same signature
 * as `evaluateHide` above. `expressions.hide` functions are re-wrapped by Formly before they ever
 * reach `parseExpressions` (`(...args) => expr(field, args[3])`, ngx-formly-core.mjs:471-473),
 * which is why `evaluateHide` only needs the field. `hideExpression` is handed to
 * `parseExpressions` untouched, so it is called with Formly's raw expression arguments —
 * `(model, formState, field, ignoreCache)` — per its own type
 * (`hideExpression?: boolean | string | ((model, formState, field?) => boolean)`,
 * `@ngx-formly/core/lib/models/fieldconfig.d.ts:95`). `field.model` / `field.options.formState`
 * are read fresh off the passed-in field (not closed over), matching how Formly itself sources
 * them at evaluation time.
 */
function evaluateHideExpression(hideExpression: unknown, field: FormlyFieldConfig): boolean {
  if (typeof hideExpression !== 'function') {
    return !!hideExpression;
  }
  const expr = hideExpression as (model: unknown, formState: unknown, field?: FormlyFieldConfig) => boolean;
  return !!expr(field.model, field.options?.formState, field);
}

/**
 * Stamps `dd-field-readonly` on read-only fields so the read-only fill in
 * `styles/components/_forms.scss` reaches every field family that still renders its own control.
 *
 * The stylesheet already recognises a native `[readonly]` on the control, which covers the
 * text-shaped families. It cannot see the ones that model read-only in TypeScript instead —
 * `mat-select`, the pickers, the composite fields — and those are exactly the fields where a
 * read-only value is most likely to be mistaken for an editable one. The class is set on
 * `field.className` (the `<formly-field>` element) rather than on the `<mat-form-field>`, which
 * Formly does not expose: `--dd-field-bg` inherits, so an ancestor is enough for the token
 * remap to land on the wrapper.
 *
 * Set through `expressions` rather than assigned once, because `props.readonly` is routinely
 * expression-driven (a field that unlocks after another is filled). A static className would
 * freeze the first evaluation.
 */
function stampReadonlyMarker(field: FormlyFieldConfig): void {
  // Containers reach this branch as well, and the fill inherits: stamping a group would recess
  // every descendant, including the editable ones.
  if (field.fieldGroup) {
    return;
  }

  // A configured className expression is authoritative — it may already compose classes of its
  // own, and overwriting it would drop them.
  if (field.expressions?.['className']) {
    return;
  }

  const baseClassName = field.className ?? '';
  if (baseClassName.split(/\s+/).includes(READONLY_FIELD_CLASS)) {
    return;
  }

  field.expressions = {
    ...field.expressions,
    className: () =>
      clearableIsReadOnly(field.props) ? `${baseClassName} ${READONLY_FIELD_CLASS}`.trim() : baseClassName,
  };
}
